import React, { useEffect, useState, useRef } from "react";
import {
  FaTrash,
  FaFilter,
  FaTimes,
  FaPlus,
  FaQrcode,
  FaCheckCircle,
  FaTimesCircle,
  FaEdit,
  FaSave,
  FaUpload,
  FaImage,
} from "react-icons/fa";
import Swal from "../../utils/swalCompat";
import PageHeader from "../../components/PageHeader";
import {
  addQR,
  QrList,
  updateQr,
  updateQrStatus,
  deleteQr,
} from "../../api/endpoints";

const QrModal = ({ show, setShow, editData, onSuccess, imagePath }) => {
  const [formData, setFormData] = useState({
    qrName: "",
    qrImage: "",
    status: "INACTIVE",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadMode, setUploadMode] = useState("file");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (show) {
      if (editData) {
        const fullImage = editData.qrImage?.startsWith("http")
          ? editData.qrImage
          : editData.qrImage?.startsWith("data:")
          ? editData.qrImage
          : `${imagePath || ""}${editData.qrImage}`;

        setFormData({
          qrName: editData.qrName || "",
          qrImage: editData.qrImage || "",
          status: editData.status || "INACTIVE",
        });
        setPreview(fullImage);
        setFile(null);
        setUploadMode("file");
      } else {
        setFormData({ qrName: "", qrImage: "", status: "INACTIVE" });
        setPreview("");
        setFile(null);
        setUploadMode("file");
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [show, editData, imagePath]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "qrImage" && uploadMode === "url") setPreview(value);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      Swal.fire("Error", "Please select a valid image file", "error");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      Swal.fire("Error", "Image size should be less than 2MB", "error");
      return;
    }

    setFile(f);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleRemoveImage = () => {
    setPreview("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ============ SUBMIT ============
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.qrName.trim()) {
      Swal.fire("Error", "Please enter QR name", "error");
      return;
    }

    // Add mode: image required
    if (!editData) {
      if (uploadMode === "file" && !file) {
        Swal.fire("Error", "Please upload a QR image", "error");
        return;
      }
      if (uploadMode === "url" && !formData.qrImage.trim()) {
        Swal.fire("Error", "Please provide a QR image URL", "error");
        return;
      }
    }

    try {
      setSaving(true);

      const payload = new FormData();
      payload.append("qrName", formData.qrName.trim());

      if (file) {
        payload.append("qrImage", file);
      } else if (uploadMode === "url" && formData.qrImage.trim()) {
        payload.append("qrImage", formData.qrImage.trim());
      }

      // ✅ ADD or UPDATE based on editData
      const res = editData
        ? await updateQr(editData._id, payload)
        : await addQR(payload);

      const isSuccess =
        res?.success === true ||
        res?.status === true ||
        res?.status === 200 ||
        res?.statusCode === 200;

      if (isSuccess) {
        const createdItem =
          res?.data || res?.result || res?.qrData || res?.qr || null;

        onSuccess?.(createdItem, editData ? "update" : "add");

        Swal.fire({
          icon: "success",
          title: editData ? "Updated!" : "Added!",
          text:
            res?.message ||
            (editData
              ? "QR updated successfully"
              : "QR added successfully"),
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        setShow(false);
      } else {
        Swal.fire(
          "Error",
          res?.message || "Failed to save QR code",
          "error"
        );
      }
    } catch (error) {
      console.error("QR save error:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
      tabIndex="-1"
      onClick={() => setShow(false)}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0" style={{ borderRadius: 14 }}>
          <div
            className="modal-header border-0 text-white"
            style={{
              background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
              padding: "16px 20px",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: 36,
                  height: 36,
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                <FaQrcode size={15} />
              </div>
              <div>
                <h5 className="modal-title fw-bold mb-0">
                  {editData ? "Edit QR Code" : "Add QR Code"}
                </h5>
                <small style={{ opacity: 0.85, fontSize: "0.72rem" }}>
                  {editData
                    ? "Update QR details"
                    : "Upload a new QR code for payments"}
                </small>
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setShow(false)}
            ></button>
          </div>

          <div className="modal-body p-4">
            <div className="row g-4">
              <div className="col-md-5 col-12">
                <label className="form-label fw-semibold">
                  QR Image {!editData && <span className="text-danger">*</span>}
                </label>

                <div className="d-flex gap-2 mb-3">
                  <button
                    type="button"
                    className={`btn btn-sm flex-fill ${
                      uploadMode === "file" ? "btn-primary" : "btn-light border"
                    }`}
                    onClick={() => setUploadMode("file")}
                    disabled={saving}
                  >
                    <FaUpload size={11} className="me-1" /> Upload
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm flex-fill ${
                      uploadMode === "url" ? "btn-primary" : "btn-light border"
                    }`}
                    onClick={() => setUploadMode("url")}
                    disabled={saving}
                  >
                    <FaImage size={11} className="me-1" /> URL
                  </button>
                </div>

                {uploadMode === "file" ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={handleFileChange}
                      disabled={saving}
                    />
                    <small className="text-muted d-block mt-1">
                      Max 2MB — PNG, JPG, JPEG
                      {editData && " (leave empty to keep existing)"}
                    </small>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      name="qrImage"
                      className="form-control"
                      placeholder="https://example.com/qr.png"
                      value={formData.qrImage}
                      onChange={handleChange}
                      disabled={saving}
                    />
                    <small className="text-muted d-block mt-1">
                      Paste QR image URL
                    </small>
                  </>
                )}

                <div
                  className="mt-3 d-flex align-items-center justify-content-center rounded-3 position-relative"
                  style={{
                    background: "#F8FAFC",
                    border: "2px dashed #CBD5E1",
                    minHeight: 200,
                    padding: 12,
                  }}
                >
                  {preview ? (
                    <>
                      <img
                        src={preview}
                        alt="QR Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 180,
                          objectFit: "contain",
                          borderRadius: 8,
                        }}
                        onError={() => setPreview("")}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute"
                        style={{ top: 8, right: 8 }}
                        onClick={handleRemoveImage}
                        title="Remove image"
                      >
                        <FaTimes size={12} />
                      </button>
                    </>
                  ) : (
                    <div className="text-center text-muted">
                      <FaQrcode size={36} style={{ opacity: 0.4 }} />
                      <p className="small mb-0 mt-2">
                        QR preview will appear here
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-7 col-12">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    QR Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="qrName"
                    className="form-control form-control-lg"
                    placeholder="e.g. Ram Rummy Official QR"
                    value={formData.qrName}
                    onChange={handleChange}
                    disabled={saving}
                    autoFocus
                  />
                  <small className="text-muted d-block mt-1">
                    Display name for admin identification
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    name="status"
                    className="form-select form-select-lg"
                    value={formData.status}
                    onChange={handleChange}
                    disabled={saving}
                  >
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="ACTIVE">ACTIVE</option>
                  </select>
                  <small className="d-block mt-1">
                    {formData.status === "ACTIVE" ? (
                      <span className="text-success">
                        <FaCheckCircle size={11} /> Active — users can use this
                        QR
                      </span>
                    ) : (
                      <span className="text-danger">
                        <FaTimesCircle size={11} /> Inactive — users can't use
                        this QR
                      </span>
                    )}
                  </small>
                </div>

                <div
                  className="p-3 rounded-3 small"
                  style={{ background: "#EFF6FF", color: "#1D4ED8" }}
                >
                  <strong>💡 Tip:</strong> QR image ko clean aur high resolution
                  me upload karein taaki users easily scan kar sakein.
                </div>
              </div>
            </div>
          </div>

          <div
            className="modal-footer border-0"
            style={{ padding: "12px 20px 18px" }}
          >
            <button
              type="button"
              className="btn btn-light border"
              onClick={() => setShow(false)}
              disabled={saving}
              style={{ fontWeight: 600 }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleSubmit}
              disabled={saving}
              style={{
                background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                border: "none",
                fontWeight: 600,
                paddingLeft: 20,
                paddingRight: 20,
              }}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  {editData ? <FaSave size={12} /> : <FaPlus size={12} />}
                  {editData ? "Update" : "Add QR"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const QrSetting = () => {
  const [qrList, setQrList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imagePath, setImagePath] = useState(""); // ✅ now settable

  const [showFilter, setShowFilter] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  // ============ FETCH QR LIST ============
  const fetchQrList = async () => {
    try {
      setLoading(true);
      const res = await QrList();

      // ✅ Correct path: res.data.upis
      const list = res?.data?.upis || [];
      setQrList(Array.isArray(list) ? list : []);

      // ✅ API se imagePath set karo
      if (res?.imagePath) setImagePath(res.imagePath);
    } catch (error) {
      console.error("QR list fetch error:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to load QR list",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQrList();
  }, []);

  const getImageUrl = (img) => {
    if (!img) return "";
    if (img.startsWith("http") || img.startsWith("data:")) return img;
    return `${imagePath}${img}`;
  };

  // ============ FILTER LOGIC ============
  const filteredList = qrList.filter((item) => {
    const matchesSearch =
      !search.trim() ||
      (item.qrName || "").toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const hasActiveFilters = search || statusFilter;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
  };

  // ============ ADD / EDIT ============
  const handleOpenAdd = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditData(item);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    // Just refresh from server — clean & consistent
    fetchQrList();
  };

  // ============ DELETE (API) ============
  const handleDelete = async (id, qrName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete "${qrName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deleteQr(id);

      if (res?.success !== false) {
        setQrList((prev) => prev.filter((item) => item._id !== id));

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: res?.message || "QR code deleted successfully",
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } else {
        Swal.fire("Error", res?.message || "Failed to delete", "error");
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to delete QR",
        "error"
      );
    }
  };

  // ============ STATUS TOGGLE (API) ============
  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const action = newStatus === "ACTIVE" ? "activate" : "deactivate";

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to ${action} this QR?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus === "ACTIVE" ? "#28a745" : "#d33",
      confirmButtonText: `Yes, ${action}`,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await updateQrStatus(id, newStatus);

      if (res?.success !== false) {
        setQrList((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, status: newStatus } : item
          )
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: res?.message || `QR ${action}d successfully`,
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } else {
        Swal.fire("Error", res?.message || "Failed to update status", "error");
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to update status",
        "error"
      );
    }
  };

  // ============ FORMAT DATE ============
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="lead-page">
      <PageHeader
        title="QR Management"
        subtitle="Manage all QR codes"
        actions={
          <>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleOpenAdd}
            >
              <FaPlus size={12} /> Add QR
            </button>
          </>
        }
      />

      <div className="card mb-4 card-light">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="card-title mb-0">QR List</h5>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search QR name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: "220px" }}
                />
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ width: "140px" }}
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    <FaTimes /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? "btn-primary" : "btn-dark"}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter />
              {showFilter ? " Hide" : " Filter"}
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {filteredList.length} of {qrList.length} QR codes
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {search && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Search: {search}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => setSearch("")}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Status: {statusFilter}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => setStatusFilter("")}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>QR Image</th>
                  <th>QR Name</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Loading...
                    </td>
                  </tr>
                ) : filteredList.length > 0 ? (
                  filteredList.map((item, index) => (
                    <tr key={item._id}>
                      <td>{index + 1}</td>

                      <td>
                        <div
                          className="d-flex align-items-center justify-content-center rounded-2 overflow-hidden"
                          style={{
                            width: 60,
                            height: 60,
                            background: "#F8FAFC",
                            border: "1px solid #E2E8F0",
                          }}
                        >
                          {item.qrImage ? (
                            <img
                              src={getImageUrl(item.qrImage)}
                              alt={item.qrName}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <FaQrcode size={22} className="text-muted" />
                          )}
                        </div>
                      </td>

                      <td>
                        <span className="fw-semibold">{item.qrName}</span>
                      </td>

                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <label
                            style={{
                              position: "relative",
                              display: "inline-block",
                              width: "50px",
                              height: "24px",
                              marginBottom: 0,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.status === "ACTIVE"}
                              onChange={() =>
                                handleStatusToggle(item._id, item.status)
                              }
                              style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span
                              style={{
                                position: "absolute",
                                cursor: "pointer",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor:
                                  item.status === "ACTIVE" ? "#4CAF50" : "#ccc",
                                transition: ".4s",
                                borderRadius: "34px",
                              }}
                            >
                              <span
                                style={{
                                  position: "absolute",
                                  height: "16px",
                                  width: "16px",
                                  left:
                                    item.status === "ACTIVE" ? "26px" : "4px",
                                  bottom: "4px",
                                  backgroundColor: "white",
                                  transition: ".4s",
                                  borderRadius: "50%",
                                }}
                              />
                            </span>
                          </label>
                          <span
                            className={`badge ${
                              item.status === "ACTIVE"
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                            style={{ minWidth: "70px" }}
                          >
                            {item.status}
                          </span>
                        </div>
                      </td>

                      <td>{formatDate(item.createdAt)}</td>

                      <td>
                        <div className="d-flex gap-2 justify-content-center">
                          <button
                            className="btn btn-sm btn-info custum_edit text-white"
                            onClick={() => handleOpenEdit(item)}
                            title="Edit QR"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(item._id, item.qrName)}
                            title="Delete QR"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      {hasActiveFilters
                        ? "No QR codes found matching your filters"
                        : "No QR codes found — Add your first QR above"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <QrModal
        show={showModal}
        setShow={setShowModal}
        editData={editData}
        onSuccess={handleModalSuccess}
        imagePath={imagePath}
      />
    </div>
  );
};

export default QrSetting;